
import { useEffect, useState } from "react";
import { useTheme } from "@/hooks/use-theme";
import { supabase } from "@/integrations/supabase/client";
import { ThemeLoader } from "./ThemeLoader";
import { useThemeSystemPreference } from "@/hooks/use-theme-system";

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const { theme, isLoading } = useTheme();
  const [hasInitialized, setHasInitialized] = useState(false);
  
  // Activate system preference change detector
  useThemeSystemPreference();
  
  // Early theme application to prevent FOUC
  useEffect(() => {
    const applyEarlyTheme = async () => {
      if (hasInitialized) return;
      
      try {
        console.log("Applying early theme to prevent FOUC...");
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user?.id) {
          // For authenticated users, try to get theme from localStorage first
          const storedTheme = ThemeLoader.getUserThemeFromStorage(user.id);
          if (storedTheme) {
            console.log("Found stored theme, applying immediately:", storedTheme);
            ThemeLoader.applyThemeToDOM(storedTheme);
          }
        } else {
          // For unauthenticated users, use generic localStorage
          const storedTheme = localStorage.getItem("theme");
          if (storedTheme && ["dark", "light", "system", "ocean", "sunset", "forest"].includes(storedTheme)) {
            console.log("Found generic stored theme, applying immediately:", storedTheme);
            ThemeLoader.applyThemeToDOM(storedTheme as any);
          } else {
            // Default to light theme
            ThemeLoader.applyThemeToDOM("light");
          }
        }
        
        setHasInitialized(true);
      } catch (error) {
        console.error("Error applying early theme:", error);
        ThemeLoader.applyThemeToDOM("light");
        setHasInitialized(true);
      }
    };

    applyEarlyTheme();
  }, [hasInitialized]);

  // Apply theme changes when theme state updates
  useEffect(() => {
    if (!isLoading && theme && hasInitialized) {
      console.log("Applying theme from state:", theme);
      ThemeLoader.applyThemeToDOM(theme);
    }
  }, [theme, isLoading, hasInitialized]);

  return <>{children}</>;
};
